// Объявляем, что в window.webapi будут лежать данные
import {WebApiError, type IWebApiError} from "./WebApiError";

export type IWebApiPromise<T = unknown> = Promise<T>;

export interface IWebApiOptions {
    filter?: string;
    signal?: AbortSignal;
}

export class WebApi<R = unknown, P = object> {
    protected getWebApiInvoker(): WebApiInvoke {
        return window.webapi.invoke;
    }

    invokeWebApi<R = unknown, P = object>(endpoint: string, parameters?: P, options?: IWebApiOptions): IWebApiPromise<R> {
        if (!endpoint) {
            throw new WebApiError(`endpoint is undefined`);
        }

        return this.getWebApiInvoker()<R, P>(endpoint, parameters, options)
            .catch((e: IWebApiError) => {
                throw new WebApiError(`endpoint="${endpoint}" ${e.errorMessage}`)
                    .setError(e);
            });
    }

    static request<R = unknown, P = object>(endpoint: string, parameters?: P, options?: IWebApiOptions) {
        return new WebApi<R, P>(endpoint, parameters, options);
    }

    protected readonly endpoint: string;
    protected readonly parameters?: P;
    protected readonly options: IWebApiOptions;
    protected attempt = 0;
    protected maxAttempts = 0;
    protected request: IWebApiPromise<R> | null = null;
    protected abortController = new AbortController();

    constructor(endpoint: string, parameters?: P, options?: IWebApiOptions) {
        this.endpoint = endpoint;
        this.parameters = parameters;
        this.options = options || {};

        // Нам чтобы делать аборт, надо иметь сигнал полюбому
        this.options.signal = this.abortController.signal;
    }

    setMaxAttempts(maxAttempts: number) {
        this.maxAttempts = maxAttempts;
        return this;
    }

    invoke() {
        this.attempt = 0;
        return this.try();
    }

    abort() {
        this.abortController.abort();
    }

    protected try(): IWebApiPromise<R> {
        this.attempt++;

        const request = this.invokeWebApi<R, P>(this.endpoint, this.parameters, this.options);
        this.request = request;

        return request.catch((e: WebApiError) => {
            if (e.isServerError() && this.attempt < this.maxAttempts) {
                return this.try();
            }

            throw e;
        });
    }
}
